'use client'

import { useState } from 'react'
import Link from 'next/link'
import { BsChevronDown, BsChevronRight, BsTree, BsBuilding, BsGeoAlt, BsTicketPerforated, BsCupStraw, BsStar } from 'react-icons/bs'
import { RateCtaLabel, StarRating } from '@/components/star-rating'
import { entityPath } from '@/lib/slug'

interface Entity {
  id: string
  slug: string
  name: string
  type: string
  imageUrl?: string | null
  ratingSummary?: {
    currentRating: number
    totalRatings: number
  } | null
  children?: Entity[]
}

interface HierarchyAccordionProps {
  parks: Entity[]
  resorts: Entity[]
}

function getEntityIcon(type: string) {
  switch (type) {
    case 'PARK': return <BsTree className="w-4 h-4" />
    case 'RESORT': return <BsBuilding className="w-4 h-4" />
    case 'LAND': return <BsGeoAlt className="w-4 h-4" />
    case 'AREA': return <BsGeoAlt className="w-4 h-4" />
    case 'ATTRACTION': return <BsTicketPerforated className="w-4 h-4" />
    case 'RESTAURANT': return <BsCupStraw className="w-4 h-4" />
    default: return <BsStar className="w-4 h-4" />
  }
}

function AccordionItem({ entity, level = 0 }: { entity: Entity; level?: number }) {
  const [isOpen, setIsOpen] = useState(false)
  const hasChildren = entity.children && entity.children.length > 0
  // Rateable items are leaf nodes - but if a resort has children, treat it as expandable
  const isRateable = ['ATTRACTION', 'RESTAURANT', 'SHOW', 'ENTERTAINMENT'].includes(entity.type) ||
    (entity.type === 'RESORT' && !hasChildren)

  const paddingLeft = level * 16 + 12

  if (isRateable) {
    return (
      <div className="border-b border-slate-100 last:border-0">
        <Link
          href={entityPath(entity)}
          className="flex items-center gap-3 py-2 px-3 hover:bg-[#1E4289]/5 transition-colors"
          style={{ paddingLeft }}
        >
          <span className="text-[#1E4289]">{getEntityIcon(entity.type)}</span>
          <span className="flex-1 text-sm text-slate-700 hover:text-[#1E4289]">{entity.name}</span>
          {entity.ratingSummary && entity.ratingSummary.totalRatings > 0 && (
            <StarRating rating={entity.ratingSummary.currentRating} size="sm" showNumber />
          )}
        </Link>
        <div className="px-3 pb-2" style={{ paddingLeft }}>
          <Link href={entityPath(entity)}>
            <RateCtaLabel />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 py-3 px-3 hover:bg-[#1E4289]/5 transition-colors text-left"
        style={{ paddingLeft }}
      >
        <span className="text-slate-400">
          {isOpen ? <BsChevronDown className="w-3 h-3" /> : <BsChevronRight className="w-3 h-3" />}
        </span>
        <span className="text-[#1E4289]">{getEntityIcon(entity.type)}</span>
        <span className="flex-1 font-medium text-slate-800">{entity.name}</span>
        {hasChildren && (
          <span className="text-xs text-slate-400">
            {entity.children!.length} items
          </span>
        )}
      </button>
      
      {isOpen && hasChildren && (
        <div className="bg-slate-50/50">
          {entity.children!.map((child) => (
            <AccordionItem key={child.id} entity={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

export function HierarchyAccordion({ parks, resorts }: HierarchyAccordionProps) {
  return (
    <div>
      {/* Parks Section */}
      <div className="border-b border-slate-300">
        <div className="bg-[#1E4289] text-white px-4 py-2 font-semibold flex items-center gap-2">
          <BsTree className="w-4 h-4" />
          Theme Parks
        </div>
        {parks.map((park) => (
          <AccordionItem key={park.id} entity={park} />
        ))}
      </div>

      {/* Resorts Section */}
      <div>
        <div className="bg-[#1E4289] text-white px-4 py-2 font-semibold flex items-center gap-2">
          <BsBuilding className="w-4 h-4" />
          Resorts
        </div>
        {resorts.map((resort) => (
          <AccordionItem key={resort.id} entity={resort} />
        ))}
      </div>
    </div>
  )
}

